import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';
import * as RadixCheckbox from '@radix-ui/react-checkbox';
import { Check, Minus } from 'lucide-react';
import { cn } from '@/lib/cn';

export type CheckboxProps = ComponentPropsWithoutRef<typeof RadixCheckbox.Root>;

export const Checkbox = forwardRef<ElementRef<typeof RadixCheckbox.Root>, CheckboxProps>(
  function Checkbox({ className, ...rest }, ref) {
    return (
      <RadixCheckbox.Root
        ref={ref}
        className={cn(
          'relative inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px] border pointer-coarse:h-6 pointer-coarse:w-6',
          // Invisible hit-area extension: 24px visual + 2×10px = 44px floor on
          // touch, without inflating the visual box.
          "pointer-coarse:before:absolute pointer-coarse:before:-inset-2.5 pointer-coarse:before:content-['']",
          'border-line-3 bg-card transition-colors duration-press ease-out-strong',
          'data-[state=checked]:border-brand data-[state=checked]:bg-brand',
          'data-[state=indeterminate]:border-brand data-[state=indeterminate]:bg-brand',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-canvas',
          'disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        {...rest}
      >
        <RadixCheckbox.Indicator className="text-canvas">
          {rest.checked === 'indeterminate' ? (
            <Minus className="h-3 w-3" strokeWidth={3} />
          ) : (
            <Check className="h-3 w-3" strokeWidth={3} />
          )}
        </RadixCheckbox.Indicator>
      </RadixCheckbox.Root>
    );
  },
);
